import React, { useState, useEffect, useReducer } from "react";
import { useHistory } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import {
    Paper,
    Button,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    IconButton,
    TextField,
    InputAdornment,
    Chip,
    Box,
    Typography,
    Tooltip
} from "@material-ui/core";
import {
    Search as SearchIcon,
    Visibility as VisibilityIcon,
    DeleteOutline as DeleteOutlineIcon,
    ShoppingCart as ShoppingCartIcon,
    CheckCircle as CheckCircleIcon,
    Cancel as CancelIcon,
    Chat as ChatIcon,
    Share as ShareIcon,
    VolumeUp as VolumeUpIcon
} from "@material-ui/icons";
import { format, parseISO } from "date-fns";
import ptBR from "date-fns/locale/pt-BR";
import { toast } from "react-toastify";

import api from "../../services/api";
import socketConnection from "../../services/socket-io";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import OrderModal from "../../components/OrderModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import MainContainer from "../../components/MainContainer";
import toastError from "../../errors/toastError";
import useOrderNotification from "../../hooks/useOrderNotification";

const reducer = (state, action) => {
    if (action.type === "LOAD_ORDERS") {
        const orders = action.payload;
        const newOrders = [];

        orders.forEach((order) => {
            const orderIndex = state.findIndex((o) => o.id === order.id);
            if (orderIndex !== -1) {
                state[orderIndex] = order;
            } else {
                newOrders.push(order);
            }
        });

        return [...state, ...newOrders];
    }

    if (action.type === "UPDATE_ORDER") {
        const order = action.payload;
        const orderIndex = state.findIndex((o) => o.id === order.id);

        if (orderIndex !== -1) {
            state[orderIndex] = order;
            return [...state];
        } else {
            return [order, ...state];
        }
    }

    if (action.type === "DELETE_ORDER") {
        const orderId = action.payload;
        const orderIndex = state.findIndex((o) => o.id === orderId);
        if (orderIndex !== -1) {
            state.splice(orderIndex, 1);
        }
        return [...state];
    }

    if (action.type === "RESET") {
        return [];
    }
};

const useStyles = makeStyles((theme) => ({
    mainPaper: {
        flex: 1,
        padding: theme.spacing(1),
        overflowY: "scroll",
        ...theme.scrollbarStyles,
    },
    searchPaper: {
        padding: theme.spacing(1),
        marginBottom: theme.spacing(1),
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(2)
    },
    pendingRow: {
        animation: "$blink 1.5s linear infinite",
        backgroundColor: "#ffebee !important"
    },
    "@keyframes blink": {
        "0%, 100%": { opacity: 1 },
        "50%": { opacity: 0.5 }
    },
    acceptButton: {
        color: theme.palette.success.main
    },
    cancelButton: {
        color: theme.palette.error.main
    },
    chatButton: {
        color: theme.palette.grey[600]
    },
    shareButton: {
        color: theme.palette.warning.main
    },
    soundButton: {
        position: "fixed",
        bottom: theme.spacing(3),
        right: theme.spacing(3),
        zIndex: 1000,
        animation: "$pulse 2s infinite"
    },
    "@keyframes pulse": {
        "0%": { transform: "scale(1)", boxShadow: "0 0 0 0 rgba(255, 152, 0, 0.7)" },
        "50%": { transform: "scale(1.05)", boxShadow: "0 0 0 10px rgba(255, 152, 0, 0)" },
        "100%": { transform: "scale(1)", boxShadow: "0 0 0 0 rgba(255, 152, 0, 0)" }
    }
}));

const Orders = () => {
    const classes = useStyles();
    const history = useHistory();

    const [loading, setLoading] = useState(false);
    const [pageNumber, setPageNumber] = useState(1);
    const [searchParam, setSearchParam] = useState("");
    const [orders, dispatch] = useReducer(reducer, []);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [orderModalOpen, setOrderModalOpen] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [deletingOrder, setDeletingOrder] = useState(null);
    const [cancellingOrder, setCancellingOrder] = useState(null);
    const [hasMore, setHasMore] = useState(false);
    const [count, setCount] = useState(0);
    const [shareModalOpen, setShareModalOpen] = useState(false);

    // Hook de notificação - toca som automaticamente
    useOrderNotification();

    useEffect(() => {
        dispatch({ type: "RESET" });
        setPageNumber(1);
    }, [searchParam]);

    useEffect(() => {
        setLoading(true);
        const delayDebounceFn = setTimeout(() => {
            const fetchOrders = async () => {
                try {
                    const { data } = await api.get("/orders", {
                        params: { searchParam, pageNumber },
                    });
                    dispatch({ type: "LOAD_ORDERS", payload: data.orders });
                    setHasMore(data.hasMore);
                    setCount(data.count);
                    setLoading(false);
                } catch (err) {
                    toastError(err);
                }
            };
            fetchOrders();
        }, 500);
        return () => clearTimeout(delayDebounceFn);
    }, [searchParam, pageNumber]);

    // Socket.IO listener
    useEffect(() => {
        const socket = socketConnection();

        socket.on("order", (data) => {
            console.log("[Orders] Socket event received:", data);
            if (data.action === "create" || data.action === "accept" || data.action === "cancel") {
                dispatch({ type: "UPDATE_ORDER", payload: data.order });
            }
        });

        return () => {
            socket.disconnect();
        };
    }, []);

    const handleSearch = (event) => {
        setSearchParam(event.target.value.toLowerCase());
    };

    const handleOpenOrderModal = (order) => {
        setSelectedOrder(order);
        setOrderModalOpen(true);
    };

    const handleCloseOrderModal = () => {
        setSelectedOrder(null);
        setOrderModalOpen(false);
    };

    const handleAcceptOrder = async (orderId) => {
        try {
            await api.put(`/orders/${orderId}/accept`);
            toast.success("Pedido aceito com sucesso!");
        } catch (err) {
            toastError(err);
        }
    };

    const handleOpenCancelModal = (order) => {
        setCancellingOrder(order);
        setConfirmOpen(true);
    };

    const handleCancelOrder = async () => {
        try {
            await api.put(`/orders/${cancellingOrder.id}/cancel`);
            toast.success("Pedido cancelado!");
            setConfirmOpen(false);
            setCancellingOrder(null);
        } catch (err) {
            toastError(err);
        }
    };

    const handleOpenChat = (order) => {
        if (order.ticketId) {
            history.push(`/tickets/${order.ticketId}`);
        } else {
            toast.error("Ticket não encontrado para este pedido");
        }
    };

    const handleShareOrder = async (order) => {
        const deliveryNumber = prompt("Digite o número do motoboy (com DDD, apenas números):");
        if (!deliveryNumber) return;

        try {
            await api.post(`/orders/${order.id}/share`, { deliveryNumber });
            toast.success("Pedido compartilhado com sucesso!");
        } catch (err) {
            toastError(err);
        }
    };

    const handleDeleteOrder = async (orderId) => {
        try {
            await api.delete(`/orders/${orderId}`);
            toast.success("Pedido excluído!");
            dispatch({ type: "DELETE_ORDER", payload: orderId });
        } catch (err) {
            toastError(err);
        }
    };

    const loadMore = () => {
        setPageNumber((prevState) => prevState + 1);
    };

    const handleScroll = (e) => {
        if (!hasMore || loading) return;
        const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
        if (scrollHeight - (scrollTop + 100) < clientHeight) {
            loadMore();
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case "pending": return "secondary";
            case "accepted": return "primary";
            case "active": return "default";
            case "completed": return "primary";
            case "cancelled": return "default";
            default: return "default";
        }
    };

    const getStatusLabel = (status) => {
        switch (status) {
            case "pending": return "Pendente";
            case "accepted": return "Aceito";
            case "active": return "Ativo";
            case "completed": return "Concluído";
            case "cancelled": return "Cancelado";
            default: return status;
        }
    };

    return (
        <MainContainer>
            <MainHeader>
                <Title>Pedidos ({count})</Title>
                <MainHeaderButtonsWrapper>
                    <TextField
                        placeholder="Buscar pedido..."
                        type="search"
                        value={searchParam}
                        onChange={handleSearch}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon style={{ color: "gray" }} />
                                </InputAdornment>
                            ),
                        }}
                    />
                </MainHeaderButtonsWrapper>
            </MainHeader>
            <Paper
                className={classes.mainPaper}
                variant="outlined"
                onScroll={handleScroll}
            >
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell align="center">#</TableCell>
                            <TableCell>Cliente</TableCell>
                            <TableCell>Telefone</TableCell>
                            <TableCell align="center">Status</TableCell>
                            <TableCell align="center">Total</TableCell>
                            <TableCell align="center">Data</TableCell>
                            <TableCell align="center">Ações</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        <>
                            {orders.map((order) => (
                                <TableRow
                                    key={order.id}
                                    className={order.status === "pending" ? classes.pendingRow : ""}
                                >
                                    <TableCell align="center">{order.id}</TableCell>
                                    <TableCell>{order.contactName}</TableCell>
                                    <TableCell>{order.contactNumber}</TableCell>
                                    <TableCell align="center">
                                        <Chip
                                            size="small"
                                            label={getStatusLabel(order.status)}
                                            color={getStatusColor(order.status)}
                                        />
                                    </TableCell>
                                    <TableCell align="center">
                                        R$ {parseFloat(order.totalValue).toFixed(2)}
                                    </TableCell>
                                    <TableCell align="center">
                                        {format(parseISO(order.createdAt), "dd/MM/yyyy HH:mm", { locale: ptBR })}
                                    </TableCell>
                                    <TableCell align="center">
                                        {order.status === "pending" && (
                                            <>
                                                <Tooltip title="Aceitar Pedido">
                                                    <IconButton
                                                        size="small"
                                                        className={classes.acceptButton}
                                                        onClick={() => handleAcceptOrder(order.id)}
                                                    >
                                                        <CheckCircleIcon />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Cancelar Pedido">
                                                    <IconButton
                                                        size="small"
                                                        className={classes.cancelButton}
                                                        onClick={() => handleOpenCancelModal(order)}
                                                    >
                                                        <CancelIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            </>
                                        )}
                                        <Tooltip title="Visualizar Detalhes">
                                            <IconButton
                                                size="small"
                                                onClick={() => handleOpenOrderModal(order)}
                                            >
                                                <VisibilityIcon />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Abrir Conversa">
                                            <IconButton
                                                size="small"
                                                className={classes.chatButton}
                                                onClick={() => handleOpenChat(order)}
                                            >
                                                <ChatIcon />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Compartilhar com Motoboy">
                                            <IconButton
                                                size="small"
                                                className={classes.shareButton}
                                                onClick={() => handleShareOrder(order)}
                                            >
                                                <ShareIcon />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))}
                            {loading && <TableRowSkeleton columns={7} />}
                        </>
                    </TableBody>
                </Table>
            </Paper>
            <OrderModal
                open={orderModalOpen}
                onClose={handleCloseOrderModal}
                order={selectedOrder}
            />
            <ConfirmationModal
                title="Cancelar Pedido"
                open={confirmOpen}
                onClose={() => setConfirmOpen(false)}
                onConfirm={handleCancelOrder}
            >
                Tem certeza que deseja cancelar o pedido #{cancellingOrder?.id}?
                <br />
                <strong>O cliente será notificado automaticamente.</strong>
            </ConfirmationModal>
        </MainContainer>
    );
};

export default Orders;
